import { OrgData } from '@rolebase/shared/model/OrgData'
import { RoleLink } from '@rolebase/shared/model/roleLinks'
import {
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
} from 'd3-force'

// Links view (#107, phase 4): the web of the roles citing each other. A pure
// layout: which nodes are drawn, where, and the lines between them.
// RoleLinksView draws it.

export interface LinkNode {
  id: string
  name: string
  // A circle holds the roles, a role is one of the cited or citing roles
  kind: 'circle' | 'role'
  hue: number | null
  x: number
  y: number
  r: number
}

export interface LinkEdge {
  id: string
  kind: 'skeleton' | 'citation'
  // The two nodes it joins, so a moved node redraws its lines
  fromId: string
  toId: string
  x1: number
  y1: number
  x2: number
  y2: number
}

export interface RoleLinksLayout {
  nodes: LinkNode[]
  edges: LinkEdge[]
}

const CIRCLE_R = 38
const ROLE_R = 20
// Distances the forces pull towards: a role close to its circle, two circles
// further apart, a citation loose enough to let the web breathe
const MEMBERSHIP_DISTANCE = 90
const SKELETON_DISTANCE = 220
const CITATION_DISTANCE = 260
// Ticks run in one go: the web is drawn settled, and the same org always comes
// out the same way (d3's starting positions are deterministic)
const TICKS = 400

interface SimNode extends LinkNode {
  index?: number
}
interface SimLink {
  source: string | SimNode
  target: string | SimNode
  distance: number
}

export function layoutRoleLinks(
  org: OrgData,
  links: readonly RoleLink[]
): RoleLinksLayout {
  if (links.length === 0) return { nodes: [], edges: [] }

  // 1. The roles of the web: those citing or cited. A mention points at a
  // circle (see extractRoleLinks), which is the role at its place in the org
  // chart. Its ancestors form the skeleton it hangs from
  const linkedIds = new Set(links.flatMap((link) => [link.fromId, link.toId]))
  const holderOf = new Map<string, string>()
  const nodes: SimNode[] = []
  const circleIds = new Set<string>()

  for (const linkedId of linkedIds) {
    const circle = org.getCircle(linkedId)
    if (!circle) continue
    nodes.push({
      id: circle.id,
      name: org.getRole(circle.roleId)?.name ?? '',
      kind: 'role',
      hue: org.getColor(circle.id),
      x: 0,
      y: 0,
      r: ROLE_R,
    })
    if (circle.parentId) {
      holderOf.set(circle.id, circle.parentId)
      for (const parent of org.andParentsOf(circle.parentId)) {
        circleIds.add(parent.id)
      }
    }
  }

  for (const circleId of circleIds) {
    const circle = org.getCircle(circleId)
    // A circle already drawn as a linked role is not drawn twice
    if (!circle || nodes.some((node) => node.id === circleId)) continue
    nodes.push({
      id: circleId,
      name: org.getRole(circle.roleId)?.name ?? '',
      kind: 'circle',
      hue: org.getColor(circleId),
      x: 0,
      y: 0,
      r: CIRCLE_R,
    })
  }

  // 2. The lines: the skeleton (a circle to its parent, a role to its circle)
  // and the citations
  const byId = new Map(nodes.map((node) => [node.id, node]))
  const simLinks: SimLink[] = []
  const edges: Array<{
    id: string
    kind: LinkEdge['kind']
    a: string
    b: string
  }> = []

  for (const circleId of circleIds) {
    const parentId = org.getCircle(circleId)?.parentId
    if (!parentId || !byId.has(parentId)) continue
    simLinks.push({
      source: circleId,
      target: parentId,
      distance: SKELETON_DISTANCE,
    })
    edges.push({
      id: `s-${circleId}`,
      kind: 'skeleton',
      a: circleId,
      b: parentId,
    })
  }
  for (const [nodeId, holderId] of holderOf) {
    if (!byId.has(holderId)) continue
    simLinks.push({
      source: nodeId,
      target: holderId,
      distance: MEMBERSHIP_DISTANCE,
    })
    edges.push({ id: `m-${nodeId}`, kind: 'skeleton', a: nodeId, b: holderId })
  }
  for (const { fromId, toId } of links) {
    if (!byId.has(fromId) || !byId.has(toId)) continue
    simLinks.push({ source: fromId, target: toId, distance: CITATION_DISTANCE })
    edges.push({
      id: `c-${fromId}-${toId}`,
      kind: 'citation',
      a: fromId,
      b: toId,
    })
  }

  // 3. The web settles in one go, then it is frozen
  const simulation = forceSimulation(nodes)
    .force(
      'link',
      forceLink<SimNode, SimLink>(simLinks)
        .id((node) => node.id)
        .distance((link) => link.distance)
        .strength(0.6)
    )
    .force('charge', forceManyBody().strength(-600))
    .force(
      'collide',
      forceCollide<SimNode>((node) => node.r + 12)
    )
    .force('x', forceX(0).strength(0.03))
    .force('y', forceY(0).strength(0.03))
    .stop()
  simulation.tick(TICKS)

  return {
    nodes: nodes.map(({ index: _index, ...node }) => node),
    edges: edges.map(({ id, kind, a, b }) => {
      const from = byId.get(a)!
      const to = byId.get(b)!
      return {
        id,
        kind,
        fromId: a,
        toId: b,
        x1: from.x,
        y1: from.y,
        x2: to.x,
        y2: to.y,
      }
    }),
  }
}
